import { Link, useParams } from 'react-router-dom';
import { useAthleteLessons, OUTCOME_LABEL } from '../api/use-athlete-lessons.js';
import { NoteRead } from '../components/note-read.js';
import { when } from '../components/when.js';
import { LessonVideos } from '../components/lesson-videos.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * One of the athlete's own lessons (board "Noa · a lesson that took place",
 * approved 16 October). Read only: the facts, the plan, and the coach's note
 * once the lesson is closed - the server leaves the note out until then.
 *
 * Plan before note, as on the parent's page (ordered 13 October): the plan is
 * what the lesson will do, the note what it did. No Cancel: cancelling is the
 * parent's.
 *
 * Then the athlete's videos (step 7a): Upload lives here, on the lesson, not
 * in a tab (ruled 16 October).
 */
export function MyLessonRoute() {
  const { lessonId } = useParams();
  const state = useAthleteLessons();

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  const lesson = state.lessons.find((candidate) => candidate.lesson_id === lessonId);
  if (!lesson) {
    return (
      <section className="panel">
        <h2>Not one of yours</h2>
        <p className="empty">No lesson with that link is yours.</p>
        <Link className="primary" to="/my">
          Back to my lessons
        </Link>
      </section>
    );
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/my" aria-label="Back to my lessons">
          ‹
        </Link>
        <div>
          <h2>{when(lesson.start)}</h2>
          <span className="detail">{OUTCOME_LABEL[lesson.outcome]}</span>
        </div>
      </div>

      <Fact label="Coach" value={lesson.coach_name || '—'} />
      <Fact label="Lesson" value={lesson.product_name ?? lesson.service_name} />
      <Fact label="Where" value={lesson.facility_name} />
      {lesson.swing_count !== null ? <Fact label="Swings" value={String(lesson.swing_count)} /> : null}

      {lesson.practice_plan ? (
        <>
          <span className="section-label">Practice plan</span>
          <NoteRead html={lesson.practice_plan} />
        </>
      ) : null}

      {lesson.notes ? (
        <>
          <span className="section-label">Note</span>
          <NoteRead html={lesson.notes} />
        </>
      ) : null}

      <LessonVideos lessonId={lesson.lesson_id} coachName={lesson.coach_name} />
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}
